import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";

import Header from "@/components/Header";
import SiteFooter from "@/components/SiteFooter";
import ProtectedRoute from "@/components/ProtectedRoute";
import PaidRoute from "@/components/PaidRoute";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Agencies from "@/pages/Agencies";
import AgencyDetail from "@/pages/AgencyDetail";
import Network from "@/pages/Network";
import Connections from "@/pages/Connections";
import Profile from "@/pages/Profile";
import RightsHub from "@/pages/RightsHub";
import Upgrade from "@/pages/Upgrade";
import PaymentSuccess from "@/pages/PaymentSuccess";
import CategoryPage from "@/pages/CategoryPage";
import RightsLibrary from "@/pages/RightsLibrary";
import RightsTopicPage from "@/pages/RightsTopicPage";
import CopyrightLawsPage from "@/pages/CopyrightLawsPage";
import TranslationHub from "@/pages/TranslationHub";
import BrandingHub from "@/pages/BrandingHub";
import BrandingTierPage from "@/pages/BrandingTierPage";
import OrchestrationPage from "@/pages/OrchestrationPage";
import LegalPage from "@/pages/LegalPage";
import Sitemap from "@/pages/Sitemap";
import FloatingTranslate from "@/components/FloatingTranslate";
import ScrollToTop from "@/components/ScrollToTop";
import ErrorBoundary from "@/components/ErrorBoundary";

function HomeRedirect() {
  // Always render Landing for `/`, regardless of auth state.
  // Prior versions redirected signed-in users to /dashboard, but /dashboard
  // itself redirects to /, which produced an infinite <Navigate replace>
  // loop and blew past the 100-call replaceState throttle.
  return <Landing />;
}

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Header />
          <ScrollToTop />
          <Toaster
            theme="dark"
            position="top-right"
            toastOptions={{
              style: {
                background: "rgba(10, 12, 16, 0.95)",
                border: "1px solid rgba(212, 175, 55, 0.3)",
                color: "#F8FAFC",
                backdropFilter: "blur(12px)",
              },
            }}
          />
          <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/dashboard" element={<PaidRoute><Dashboard /></PaidRoute>} />
            <Route path="/agencies" element={<PaidRoute><Agencies /></PaidRoute>} />
            <Route path="/agencies/:id" element={<PaidRoute><AgencyDetail /></PaidRoute>} />
            <Route path="/network" element={<PaidRoute><Network /></PaidRoute>} />
            <Route path="/connections" element={<PaidRoute><Connections /></PaidRoute>} />
            <Route path="/profile" element={<PaidRoute><Profile /></PaidRoute>} />
            <Route path="/rights/:category" element={<PaidRoute><RightsHub /></PaidRoute>} />
            <Route path="/upgrade" element={<ProtectedRoute><Upgrade /></ProtectedRoute>} />
            <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
            <Route path="/category/:slug" element={<PaidRoute><CategoryPage /></PaidRoute>} />
            <Route path="/rights-library" element={<PaidRoute><RightsLibrary /></PaidRoute>} />
            <Route path="/rights-topic/:slug" element={<PaidRoute><RightsTopicPage /></PaidRoute>} />
            <Route path="/copyright-laws" element={<CopyrightLawsPage />} />
            <Route path="/translate" element={<PaidRoute><TranslationHub /></PaidRoute>} />
            <Route path="/branding" element={<PaidRoute><BrandingHub /></PaidRoute>} />
            <Route path="/branding/:tier" element={<PaidRoute><BrandingTierPage /></PaidRoute>} />
            <Route path="/orchestration" element={<PaidRoute><OrchestrationPage /></PaidRoute>} />
            <Route path="/licensing" element={<PaidRoute><Navigate to="/rights-topic/licensing" replace /></PaidRoute>} />
            <Route path="/legal/:slug" element={<LegalPage />} />
            <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
            <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
            <Route path="/disclaimer" element={<Navigate to="/legal/disclaimer" replace />} />
            <Route path="/refund" element={<Navigate to="/legal/refund" replace />} />
            <Route path="/sitemap" element={<Sitemap />} />
            <Route path="/rights/master-map" element={<Sitemap />} />
            <Route path="/table-of-contents" element={<Navigate to="/sitemap" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <SiteFooter />
          <FloatingTranslate />
        </AuthProvider>
      </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;
